import React, { useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { FileText, ListOrdered, Pencil, Trash2 } from 'lucide-react';
import ConfirmationModal from '../../Components/ConfirmationModal';
import InfoPanel from '../../Components/InfoPanel';
import Skeleton from '../../Components/Skeleton';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { useFeature } from '../../hooks/useFeatures';
import { UseAllParkingQueueEntriesReturn } from '../../hooks/parkingQueue/useAllParkingQueueEntries';
import { tenantsApi } from '../../api/tenantsApi';
import { Lease } from '../../types/leases';
import { useLeaseStairwells } from '../../hooks/leases';
import { Tenant, TenantInput, formatTenantAddress } from '../../types/tenants';
import { translate } from '../../types/EnumTranslations';
import { formatCurrency } from '../../Helpers/FormatUtils';
import { activeYearlyIncome, tenantHoldsContract } from '../../Helpers/leaseUtils';
import { HeldKeysList, HeldKeysRegister } from '../KeyRegister/HeldKeysSection';
import SectionTitle from '../WorkOrders/components/shared/SectionTitle';
import TenantModal from './TenantModal';
import TenantQueueEntryCard from './TenantQueueEntryCard';
import TenantLeaseRows from './TenantLeaseRows';
import { tenantLeaseGroups } from './tenantsView';

interface TenantDetailPaneProps {
    tenant: Tenant;
    /** Every lease; the pane picks the tenant's own. */
    leases: Lease[];
    leasesLoading: boolean;
    buildingName: (buildingKey: string) => string;
    /** Every queue entry and the key register, loaded once by the page so switching tenant reads nothing. */
    queue: UseAllParkingQueueEntriesReturn;
    keyRegister: HeldKeysRegister;
    refreshTenants: () => Promise<void>;
    /** The tenant was removed for good (not anonymised): the page clears the selection. */
    onDeleted: () => void;
}

/** One tenant in the Hyresgäster pane: details, leases, queue places and keys. */
const TenantDetailPane: React.FC<TenantDetailPaneProps> = ({ tenant, leases, leasesLoading, buildingName, queue, keyRegister, refreshTenants, onDeleted }) => {
    const isAdmin = useIsAdmin();
    const term = useTenantTerm();
    const parkingEnabled = useFeature('parking');
    const keyRegisterEnabled = useFeature('keyRegister');
    const entries = useMemo(() => queue.entries.filter(entry => entry.tenantKey === tenant.key), [queue.entries, tenant.key]);
    const [showModal, setShowModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    const { current, ended } = useMemo(() => tenantLeaseGroups(leases, tenant.key), [leases, tenant.key]);
    const tenantLeases = useMemo(() => [...current, ...ended], [current, ended]);
    const { objectLabel } = useLeaseStairwells(tenantLeases);
    // Rent the tenant is liable for: leases where they are the primary or a medhyresgäst, not a medboende.
    const yearlyRent = useMemo(
        () => activeYearlyIncome(current.filter(lease => tenantHoldsContract(lease, tenant.key))),
        [current, tenant.key]
    );

    const handleUpdate = async (input: TenantInput) => {
        setSubmitting(true);
        try {
            await tenantsApi.update(tenant.key, input);
            await refreshTenants();
            setShowModal(false);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const result = await tenantsApi.delete(tenant.key);
            await refreshTenants();
            setConfirmDelete(false);
            if (result === 'deleted') {
                onDeleted();
            } else {
                setInfo('Uppgifterna har anonymiserats');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte ta bort');
            setConfirmDelete(false);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="tenant-pane">
            <div className="tenant-pane-header">
                <div className="tenant-pane-title">
                    <h2>{tenant.name}</h2>
                    <span className="tenant-pane-sub">
                        {translate(tenant.kind)}{tenant.orgNumber ? ` · ${tenant.orgNumber}` : ''}
                        {tenant.anonymized && <span className="tenant-anonymized-chip">Borttagen</span>}
                    </span>
                </div>
                {isAdmin && !tenant.anonymized && (
                    <div className="tenant-pane-actions">
                        <Button variant="outline-primary" size="sm" onClick={() => setShowModal(true)} disabled={submitting}>
                            <Pencil size={14} className="me-1" />Redigera
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={submitting}>
                            <Trash2 size={14} className="me-1" />Ta bort
                        </Button>
                    </div>
                )}
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
            {info && <Alert variant="info" dismissible onClose={() => setInfo(null)}>{info}</Alert>}

            <div className="tenant-details">
                <InfoPanel
                    items={[
                        { label: 'E-post', value: tenant.email || '-' },
                        { label: 'Telefon', value: tenant.phone || '-' },
                        { label: 'Adress', value: formatTenantAddress(tenant) || '-' },
                        { label: 'Aktiv hyra per år', value: leasesLoading ? '…' : formatCurrency(yearlyRent) },
                        ...(tenant.comment ? [{ label: 'Kommentar', value: tenant.comment }] : []),
                    ]}
                />
            </div>

            <section className="tenant-section">
                <SectionTitle icon={FileText} title="Hyresavtal" count={current.length} />
                {leasesLoading ? <Skeleton width="70%" /> : <TenantLeaseRows current={current} ended={ended} buildingName={buildingName} objectLabel={objectLabel} />}
            </section>

            {isAdmin && parkingEnabled && (
                <section className="tenant-section">
                    <SectionTitle icon={ListOrdered} title="Köplatser" count={entries.length} />
                    {queue.error && <Alert variant="danger">{queue.error}</Alert>}
                    {queue.loading ? (
                        // The empty text's own line, so nothing moves when the entries land.
                        <p className="text-muted" aria-busy="true"><Skeleton inline width="7em" /></p>
                    ) : entries.length === 0 ? (
                        <p className="text-muted">Inga köplatser.</p>
                    ) : (
                        <div className="tenant-queue-list">
                            {entries.map(entry => <TenantQueueEntryCard key={entry.key} entry={entry} />)}
                        </div>
                    )}
                </section>
            )}

            {/* Admin-only root, like the queue above. */}
            {isAdmin && keyRegisterEnabled && <HeldKeysList holder={{ kind: 'tenant', key: tenant.key }} register={keyRegister} />}

            <ConfirmationModal
                show={confirmDelete}
                title={`Ta bort ${term.singular.toLowerCase()}`}
                message="Personen tas bort helt om inget hyresavtal eller köplats finns, annars anonymiseras uppgifterna (namn, kontaktuppgifter och anteckningar i parkeringskön). Det går inte att ångra."
                confirmText="Ta bort"
                variant="danger"
                loading={submitting}
                onConfirm={handleDelete}
                onCancel={() => setConfirmDelete(false)}
            />

            <TenantModal show={showModal} onHide={() => setShowModal(false)} onSubmit={handleUpdate} tenant={tenant} submitting={submitting} />
        </div>
    );
};

export default TenantDetailPane;
